/**
 * Связь «Настроек» шапки с экраном под ней (docs/release-2026-09-19.md §5.6).
 *
 * Шапка грузит модель писателя и стиль по умолчанию сама (`Shell.tsx`) — ими подписан орган
 * «Настройки». Но стиль по умолчанию меняют ДВА места: диалог «Настройки» и кнопка «Сделать
 * по умолчанию» в редакторе стилей. Без общей связи каждое из них видело бы только своё
 * изменение: сменил умолчание в редакторе — шапка продолжает говорить «v5»; сменил в шапке —
 * редактор продолжает метить умолчанием прежнюю версию.
 *
 * Поэтому шапка отдаёт вниз ровно две вещи: `changed()` — «я поменял настройки, перечитай
 * их», и `rev` — счётчик таких изменений, по которому экран перечитывает своё. Общего стора
 * нет и не заводится: два числа и функция, а не зеркало данных шапки.
 */

import { createContext, useContext, useEffect, useRef } from 'react';

export interface ShellSettings {
  /** Растёт на каждое сохранённое изменение настроек — из шапки или с экрана. */
  readonly rev: number;
  /** Сообщить шапке, что настройки изменились: она перечитает модель и стиль. */
  readonly changed: () => void;
}

/** Вне шапки (тесты отдельных экранов) связи нет, и это законно: перечитывать некому. */
const DETACHED: ShellSettings = { rev: 0, changed: () => undefined };

export const ShellSettingsContext = createContext<ShellSettings>(DETACHED);

export function useShellSettings(): ShellSettings {
  return useContext(ShellSettingsContext);
}

/**
 * Позвать `fn`, когда шапка сообщила «настройки изменились» (вырос `rev`), — но не на первой
 * отрисовке: начальная загрузка у экрана своя. Нужна владельцам данных, которые сами шапку
 * рисуют и потому стоят ВНЕ её контекста (`WorkspacePage`, `PlanPage`): они зовут хук из
 * дочернего узла под `<Shell>` (`ShellRevWatcher`). С 2026-10-25 `rev` растит и выключатель
 * автоодобрения (docs/ai-lab.md): выключение возвращает посты из слотов на согласование, и
 * бэклог и календарь обязаны это показать без перезагрузки.
 */
export function useOnShellRev(fn: () => void): void {
  const { rev } = useShellSettings();
  const seen = useRef(rev);
  useEffect(() => {
    if (seen.current === rev) return;
    seen.current = rev;
    fn();
  }, [rev, fn]);
}

/** Узел под `<Shell>`, который зовёт `onRev` на каждый рост `rev` (см. `useOnShellRev`). */
export function ShellRevWatcher({ onRev }: { readonly onRev: () => void }): null {
  useOnShellRev(onRev);
  return null;
}
